<template>
  <div class="page-container order-info">
    <div class="main">
      <page
        :url="url"
        :form="form"
        :formRules="formRules"
        :columns="columns"
        @reset="reset"
        :isShowQuickSearch="true"
      >
        <template slot="pageHeader">
          <div class="order-summary">
            <div class="summary-item">
              {{ $t("message.orderAmountSummary") }}:
              <span> {{ totalAmount }}</span>
            </div>
            <div class="summary-item">
              {{ $t("message.orderFeeSummary") }}: <span>{{ totalFee }}</span>
            </div>
            <div class="summary-item">
              {{ $t("message.orderActualSummary") }}:
              <span>{{ totalActualAmount }}</span>
            </div>
            <div class="table-set-button-list">
              <TableColumnSetting
                :columns="columns"
                @change="handleColumnChange"
              />
            </div>
          </div>
        </template>
        <template slot="tableSearch">
          <el-form-item :label="$t('text.merchantOrderNumber')">
            <el-input
              v-model="form.merchantOrderId"
              :placeholder="$t('message.inputMerchantOrderId')"
              style="width: 240px"
              maxlength="50"
            />
          </el-form-item>
          <el-form-item :label="$t('text.orderNumber')">
            <el-input
              v-model="form.orderId"
              :placeholder="$t('message.inputOrderId')"
              style="width: 240px"
              maxlength="50"
            />
          </el-form-item>
          <el-form-item :label="$t('text.channelOrderNumber')">
            <el-input
              v-model="form.channelOrderId"
              :placeholder="$t('message.inputChannelOrderIdForQuery')"
              style="width: 240px"
              maxlength="50"
            />
          </el-form-item>

          <el-form-item :label="$t('text.orderStatus')">
            <el-select
              v-model="selectedStatuses"
              :placeholder="$t('message.chooseOne')"
              multiple
            >
              <el-option :label="$t('option.all')" value="all" />
              <el-option :label="$t('orderStatus.00')" value="00" />
              <el-option :label="$t('orderStatus.01')" value="01" />
              <el-option :label="$t('orderStatus.02')" value="02" />
              <el-option :label="$t('orderStatus.03')" value="03" />
              <el-option :label="$t('orderStatus.99')" value="99" />
              <el-option :label="$t('orderStatus.04')" value="04" />
              <el-option :label="$t('orderStatus.06')" value="06" />
              <el-option :label="$t('orderStatus.07')" value="07" />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.amount')">
            <el-input
              v-model="form.amount"
              :placeholder="$t('message.inputAmount')"
              maxlength="20"
            />
          </el-form-item>
          <el-form-item :label="$t('text.callbackStatus')">
            <el-select
              v-model="form.callbackStatus"
              style="width: 100px"
              :placeholder="$t('text.callbackStatus')"
            >
              <el-option :label="$t('option.all')" value="" />
              <el-option :label="$t('callbackStatus.00')" value="00" />
              <el-option :label="$t('text.callbackSuccess')" value="01" />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.settlementStatus')">
            <el-select
              v-model="form.settlementStatus"
              style="width: 100px"
              :placeholder="$t('text.settlementStatus')"
            >
              <el-option :label="$t('option.all')" value="" />
              <el-option :label="$t('settlementStatus.00')" value="00" />
              <el-option :label="$t('settlementStatus.01')" value="01" />
            </el-select>
          </el-form-item>

          <el-form-item
            :label="$t('text.creationTime')"
            prop="createTimeDate"
            :inline-message="true"
            :error="showCreateTimeDateError"
          >
            <el-date-picker
              v-model="createTimeDate"
              type="daterange"
              format="yyyy-MM-dd"
              value-format="yyyyMMdd"
              :range-separator="$t('text.to')"
              :start-placeholder="$t('text.startDate')"
              :end-placeholder="$t('text.endDate')"
              @change="createTimeChange"
            />
          </el-form-item>
          <el-form-item
            :label="$t('text.finishTime')"
            prop="endTimeDate"
            :inline-message="true"
            :error="showEndTimeDateError"
          >
            <el-date-picker
              v-model="endTimeDate"
              type="daterange"
              format="yyyy-MM-dd"
              value-format="yyyyMMdd"
              :range-separator="$t('text.to')"
              :start-placeholder="$t('text.startDate')"
              :end-placeholder="$t('text.endDate')"
              @change="endTimeChange"
            />
          </el-form-item>
        </template>
        <template slot="tableCollapseSearch">
          <el-form-item v-if="ROLE_TYPE.isManager()" :label="$t('text.agent')">
            <el-select
              v-model="form.agentId"
              filterable
              clearable
              :placeholder="$t('message.chooseOne')"
              popper-class="eloption"
            >
              <el-option :label="$t('option.all')" value="" />
              <el-option
                v-for="item in agentList"
                :key="item.agentId"
                :label="item.agentId + '-' + item.agentName"
                :value="item.agentId"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="ROLE_TYPE.isManager() || ROLE_TYPE.isAgent()"
            :label="$t('text.merchant')"
          >
            <el-select
              v-model="form.appId"
              filterable
              clearable
              :placeholder="$t('message.chooseOne')"
              popper-class="eloption"
            >
              <el-option :label="$t('option.all')" value="" />
              <el-option
                v-for="merchant in merchantList"
                :label="merchant.value + '-' + merchant.name"
                :key="merchant.value"
                :value="merchant.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="ROLE_TYPE.isManager()"
            :label="$t('text.paymentChannel')"
          >
            <el-select
              v-model="form.channelId"
              filterable
              clearable
              :placeholder="$t('message.chooseOne')"
            >
              <el-option :label="$t('option.all')" value="" />
              <el-option
                v-for="item in payOption"
                :key="item.channelId"
                :label="item.channelId + ' - ' + item.channelName"
                :value="item.channelId"
              />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.upstream')">
            <el-select
              v-model="form.upstream"
              style="width: 140px"
              :placeholder="$t('message.chooseOne')"
            >
              <el-option label="全部" value=""></el-option>
              <el-option label="Easypaisa" value="easypaisa"></el-option>
              <el-option label="Jazzcash" value="jazzcash"></el-option>
              <el-option label="OpayCard" value="opaycard"></el-option>
              <el-option label="SadapayCard" value="sadapaycard"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item :label="$t('text.accountNumber')">
            <el-input
              v-model="form.accountNum"
              :placeholder="$t('message.inputAccountNum')"
              style="width: 180px"
              maxlength="50"
            />
          </el-form-item>

          <el-form-item :label="$t('text.phoneNumber')">
            <el-input
              v-model="form.customerPhone"
              :placeholder="$t('message.inputPhoneNumber')"
              style="width: 180px"
              maxlength="50"
            />
          </el-form-item>
          <el-form-item :label="$t('text.name')">
            <el-input
              v-model="form.customerName"
              :placeholder="$t('message.inputName')"
              style="width: 160px"
              maxlength="50"
            />
          </el-form-item>
          <el-form-item :label="$t('text.idNumber')">
            <el-input
              v-model="form.customerCert"
              :placeholder="$t('message.inputCustomerCert')"
              style="width: 140px"
              maxlength="50"
            />
          </el-form-item>
          <el-form-item :label="$t('text.refId')" v-if="this.$store.state.isBD">
            <el-input
              v-model="form.refId"
              :placeholder="$t('message.inputRefId')"
              style="width: 160px"
              maxlength="50"
            />
          </el-form-item>
          <el-form-item :label="$t('text.merchantUserId')" v-if="this.$store.state.isBD">
            <el-input
                v-model="form.merchantUserId"
                :placeholder="$t('message.inputMerchantUserId')"
                style="width: 160px"
                maxlength="50"
            />
          </el-form-item>
          <el-form-item :label="$t('text.transactionMapped')" v-if="this.$store.state.isBD">
            <el-select
                v-model="form.transactionMapped"
                style="width: 100px"
                :placeholder="$t('text.transactionMapped')"
            >
              <el-option :label="$t('option.all')" value="" />
              <el-option :label="$t('text.mapping')" value="0" />
              <el-option :label="$t('text.waitMapping')" value="1" />
            </el-select>
          </el-form-item>
        </template>

        <template slot="buttonArea">
          <el-dropdown style="margin-right: 12px" @command="exportExcel">
            <el-button
              type="primary"
              :loading="loading"
              :disabled="exporting"
              size="small"
              v-if="
                !ROLE_TYPE.isCustomerServiceStaff() &&
                !ROLE_TYPE.isBusinessCustomerServiceManage()
              "
            >
              {{ $t("text.export") }}
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="exportDetail">
                {{ $t("text.exportToExcel") }}</el-dropdown-item
              >
              <el-dropdown-item command="exportToS3Detail">{{
                $t("text.exportToZip")
              }}</el-dropdown-item>
              <el-dropdown-item command="downloadExportZipFile">{{
                $t("text.downloadZip")
              }}</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>

          <el-button :loading="loading" type="primary" size="mini" plain @click="countAmount">
            {{ $t("text.statistics") }}
          </el-button>

          <el-button
            v-if="
              ROLE_TYPE.isManager() &&
              !ROLE_TYPE.isCustomerServiceStaff() &&
              !ROLE_TYPE.isBusinessCustomerServiceManage()
            "
            type="primary"
            size="mini"
            plain
            @click="popBatchCallbackFileSelect"
          >
            {{ $t("text.batchCallback") }}
            <input
              type="file"
              ref="callbackFile"
              class="file-input"
              @change="handleBatchCallbackUpdate"
              style="display: none"
            />
          </el-button>
          <el-button
            v-if="ROLE_TYPE.isManager()"
            type="primary"
            size="mini"
            plain
            @click="popSyncFileSelect"
          >
            {{ $t("text.batchSync") }}
            <input
              type="file"
              ref="SyncFile"
              class="file-input"
              @change="handleBulkSyncOrderStatus"
              style="display: none"
            />
          </el-button>
          <el-button
            v-if="ROLE_TYPE.isManager()"
            type="primary"
            size="mini"
            plain
            @click="popBatchBankStatementFileSelect"
          >
            {{ $t("text.bankCardCallback") }}
            <input
              type="file"
              ref="bankStatementFile"
              class="file-input"
              @change="importStatement"
              style="display: none"
            />
          </el-button>
          <el-button
            v-if="
              ROLE_TYPE.isManager() &&
              !ROLE_TYPE.isCustomerServiceStaff() &&
              !ROLE_TYPE.isBusinessCustomerServiceManage()
            "
            type="primary"
            size="mini"
            plain
            @click="handleManualSettlement"
          >
            {{ $t("text.manualSettlement") }}
          </el-button>
          <el-button
            v-if="
              ROLE_TYPE.isManager() &&
              !ROLE_TYPE.isCustomerServiceStaff() &&
              !ROLE_TYPE.isBusinessCustomerServiceManage()
            "
            type="primary"
            size="mini"
            plain
            @click="handleCreateTestingRepaymentLink"
          >
            {{ $t("text.testOrder") }}
          </el-button>
          <el-button
            v-if="ROLE_TYPE.isManager()"
            type="primary"
            size="mini"
            plain
            @click="handleOrderRestoration"
          >
            {{ $t("text.restoration") }}
          </el-button>
          <el-button
            v-if="ROLE_TYPE.isManager()"
            type="primary"
            size="mini"
            plain
            @click="handleSyncOrderStatusConfig"
          >
            {{ $t("text.syncOrderStatusConfig") }}
          </el-button>
        </template>

        <template slot="tableSlotScope" slot-scope="{ row, column }">
          <template v-if="column.prop === 'orderId'">
            <div class="orderStyle" style="margin-bottom: 0">
              <el-tag size="mini">{{ $t("text.collection") }}</el-tag>
              {{ row.orderId }}
            </div>
            <div class="orderStyle" style="margin-bottom: 0">
              <el-tag size="mini" type="success">{{
                $t("text.merchant")
              }}</el-tag>
              {{ row.merchantOrderId }}
            </div>
            <div class="orderStyle">
              <el-tag size="mini" type="warning">{{
                $t("text.channel")
              }}</el-tag>
              <span v-if="row.channelOrderId">{{ row.channelOrderId }}</span>
              <span v-else>--</span>
            </div>
          </template>
          <template v-else-if="column.prop === 'agentId'">
            <div>{{ row.agentId }}</div>
            <div>{{ row.agentName }}</div>
          </template>
          <template v-else-if="column.prop === 'appId'">
            <div>{{ row.appId }}</div>
            <div>{{ row.merchantName }}</div>
          </template>
          <template v-else-if="column.prop === 'createDate'">
            {{
              $formatter.formatDate(
                null,
                null,
                row.createDate + row.createTime,
                null
              )
            }}
          </template>
          <template v-else-if="column.prop === 'submitDate'">
            <div v-if="row.submitDate">
              {{
                $formatter.formatDate(
                    null,
                    null,
                    row.submitDate + row.submitTime,
                    null
                )
              }}
            </div>
            <div v-else>--</div>
          </template>
          <template v-else-if="column.prop === 'endDate'">
            <div v-if="row.endDate">
              {{
                $formatter.formatDate(
                  null,
                  null,
                  row.endDate + row.endTime,
                  null
                )
              }}
            </div>
            <div v-else>--</div>
          </template>
          <template v-else-if="column.prop === 'status'">
            <OrderStatusTag :status="row.status" />
          </template>
          <template
            v-else-if="
              ['createMessage', 'queryMessage', 'msg', 'sysRemark'].includes(
                column.prop
              )
            "
          >
            <div v-if="row[column.prop]">
              <el-popover
                v-if="row[column.prop] && row[column.prop].length > 10"
                placement="top-start"
                width="200"
                trigger="hover"
                :content="row[column.prop]"
              >
                <div slot="reference" class="remark">
                  {{ row[column.prop] }}
                </div>
              </el-popover>
              <div v-else>{{ row[column.prop] }}</div>
            </div>
            <span v-else>--</span>
          </template>
          <template v-else-if="column.prop === 'operate'">
            <el-button
              v-if="ROLE_TYPE.isManager()"
              type="info"
              plain
              size="mini"
              @click.stop="handleOrderLog(row)"
            >
              {{ $t("text.log") }}
            </el-button>
            <el-button
              v-if="row.status==='00'"
              type="primary"
              plain
              size="mini"
              @click.stop="orderRefresh(row)"
            >
              {{ $t("text.refresh") }}
            </el-button>
            <el-button
              v-if="['01','02'].includes(row.status)"
              type="success"
              plain
              size="mini"
              @click.stop="orderNotify(row)"
            >
              {{ $t("text.callback") }}
            </el-button>
            <el-button
              v-if="ROLE_TYPE.isManager() && !['01','02'].includes(row.status)"
              type="warning"
              size="mini"
              plain
              @click.stop="handleModify(row)"
            >
              {{ $t("text.modify") }}
            </el-button>
            <el-button
              v-if="ROLE_TYPE.isManager() && ['01','02'].includes(row.status) "
              type="danger"
              size="mini"
              plain
              @click.stop="orderReverse(row.orderId)"
            >
              {{ $t("text.reverse") }}
            </el-button>
          </template>
          <template v-else>
            {{
              row[column.prop]
                ? column.formatter
                  ? column.formatter(null, null, row[column.prop])
                  : row[column.prop]
                : "--"
            }}
          </template>
        </template>
      </page>
      <OrderModify :is-show-modify-dialog.sync="isShowModifyDialog"
                   :id="id"
                   :ref-id="refId"
                   :is-payin="true"/>
      <OrderLogDialog
        :is-show-log-dialog.sync="isShowOrderLogDialog"
        :id="id"
        :row.sync="currentOrderDetail"
      />
      <ManualSettlementDialog
        :isShowManualSettlementDialog.sync="isShowManualSettlementDialog"
      />
      <CreateTestCollectionOrderDialog
        :isShowCreateTestCollectionOrderDialog.sync="
          isShowCreateTestCollectionOrderDialog
        "
      />
      <OrderRestorationDialog
        :isShowOrderRestorationDialog.sync="isShowOrderRestorationDialog"
      />
      <SyncOrderStatusConfigDialog
        :isShowSyncOrderStatusConfigDialog.sync="
          isShowSyncOrderStatusConfigDialog
        "
      />
<!--    <OrderDetailDrawer :visible.sync="isShowOrderDetailDrawer" :data.sync="currentOrderDetail" />-->
    </div>
  </div>
</template>

<script>
import Page from "@/components/Page.vue";
import VueQrcode from "@chenfengyuan/vue-qrcode";
import moment from "moment";
import OrderModify from "./components/OrderModify.vue";
import OrderLogDialog from "./components/OrderLogDialog.vue";
import ManualSettlementDialog from "./components/ManualSettlementDialog.vue";
import OrderDetailDrawer from "./components/OrderDetailDrawer.vue";
import OrderStatusTag from "./components/OrderStatusTag.vue";
import TableColumnSetting from "@/components/TableColumnSetting.vue";
import { ROLE_TYPE } from "@/utils/Constant";
import CreateTestCollectionOrderDialog from "@/views/order/components/CreateTestCollectionOrderDialog.vue";
import OrderRestorationDialog from "@/views/order/components/OrderRestorationDialog.vue";
import SyncOrderStatusConfigDialog from "@/views/order/components/SyncOrderStatusConfigDialog.vue";

export default {
  name: "Systems",
  computed: {
    ROLE_TYPE() {
      return ROLE_TYPE;
    },
    selectedStatuses: {
      get() {
        return this.form.status ? this.form.status.split(",") : [];
      },
      set(value) {
        this.form.status = value.join(",");
      },
    },
  },
  components: {
    CreateTestCollectionOrderDialog,
    OrderRestorationDialog,
    page: Page,
    qrcode: VueQrcode,
    OrderModify: OrderModify,
    OrderLogDialog: OrderLogDialog,
    ManualSettlementDialog: ManualSettlementDialog,
    SyncOrderStatusConfigDialog: SyncOrderStatusConfigDialog,
    OrderDetailDrawer,
    OrderStatusTag,
    TableColumnSetting,
  },
  data() {
    const generateColumns = () => {
      let columns =  [
        {
          prop: "orderId",
          width: "310px",
          label: this.$t("text.orderNumber"),
          fixed: true,
          align: "center",
        },
        {
          prop: "onlineTopUpOrderId",
          width: "120px",
          label: this.$t("text.onlineTopUpOrderId"),
          align: "center",
          visible: ROLE_TYPE.isManager(),
        },
        {
          prop: "agentId",
          width: "180px",
          label: this.$t("text.agent"),
          align: "center",
          visible: ROLE_TYPE.isManager(),
        },
        {
          prop: "appId",
          width: "180px",
          label: this.$t("text.merchant"),
          align: "center",
        },
        {
          prop: "defaultChannelName",
          width: "140px",
          label: this.$t("text.defaultChannel"),
          align: "center",
        },
        {
          prop: "channelName",
          width: "180px",
          label: this.$t("text.paymentChannel"),
          align: "center",
          visible: ROLE_TYPE.isManager(),
        },
        {
          prop: "providerName",
          width: "140px",
          label: this.$t("text.paymentChannelProvider"),
          align: "center",
        },
        {
          prop: "checkoutType",
          width: "140px",
          label: this.$t("text.checkoutType"),
          align: "center",
        },
        {
          prop: "amount",
          width: "110px",
          formatter: this.$formatter.FenToYuanToThousandth,
          label: this.$t("text.orderAmount"),
          align: "center",
        },
        {
          prop: "realPayAmount",
          width: "110px",
          formatter: this.$formatter.FenToYuanToThousandth,
          label: this.$t("text.realPay"),
          align: "center",
        },
        {
          prop: "createDate",
          width: "160px",
          label: this.$t("text.creationTime"),
          align: "center",
        },
        {
          prop: "submitDate",
          width: "160px",
          label: this.$t("text.submittedTime"),
          align: "center",
        },
        {
          prop: "endDate",
          width: "160px",
          label: this.$t("text.finishTime"),
          align: "center",
        },
        {
          prop: "status",
          width: "130px",
          label: this.$t("text.orderStatus"),
          align: "center",
        },
        {
          prop: "callbackStatus",
          formatter: this.$formatter.formatCallBackStatus,
          width: "80px",
          label: this.$t("text.whetherToCallback"),
          align: "center",
        },
        {
          prop: "settlementStatus",
          formatter: this.$formatter.formatSettlementStatus,
          width: "80px",
          label: this.$t("text.whetherToSettle"),
          align: "center",
        },
        {
          prop: "settlementCycle",
          width: "80px",
          label: this.$t("text.settlementCycle"),
          align: "center",
        },
        {
          prop: "feeRate",
          width: "80px",
          label: this.$t("text.feeRate"),
          align: "center",
        },
        {
          prop: "fee",
          formatter: this.$formatter.FenToYuan3ToThousandth,
          width: "80px",
          label: this.$t("text.fee"),
          align: "center",
        },
        {
          prop: "createMessage",
          width: "160px",
          label: this.$t("text.createMessage"),
          align: "center",
        },
        {
          prop: "queryMessage",
          width: "160px",
          label: this.$t("text.queryMessage"),
          align: "center",
        },
        {
          prop: "msg",
          width: "160px",
          label: this.$t("text.message"),
          align: "center",
        },
        {
          prop: "sysRemark",
          width: "150px",
          label: this.$t("text.systemRemark"),
          align: "center",
        },
        {
          prop: "operate",
          fixed: "right",
          width: ROLE_TYPE.isManager() ? "215" : "180",
          label: this.$t("text.actions"),
          align: "center",
        },
      ];

      if (this.$store.state.isBD) {
        columns.push(
            {
              prop: "paymentType",
              width: "140px",
              label: this.$t("text.walletType"),
              align: "center",
            },
            {
              prop: "receiverAccount",
              width: "140px",
              label: this.$t("text.walletNumber"),
              align: "center",
            },
            {
              prop: "refId",
              width: "140px",
              label: this.$t("text.refId"),
              align: "center",
            },
            {
              prop: "merchantUserId",
              width: "140px",
              label: this.$t("text.merchantUserId"),
              align: "center",
            }
        );
      }

      if (this.$store.state.isPK) {
        columns.push(
            {
              prop: "accountNum",
              width: "140px",
              label: this.$t("text.accountNumber"),
              align: "center",
            },
            {
              prop: "customerPhone",
              width: "140px",
              label: this.$t("text.phoneNumber"),
              align: "center",
            },
            {
              prop: "customerName",
              width: "160px",
              label: this.$t("text.name"),
              align: "center",
            },
            {
              prop: "customerCert",
              width: "130px",
              label: this.$t("text.idNumber"),
              align: "center",
            }
        );
      }

      return columns;
    };
    return {
      url: "/order/selectList?payType=110",
      action: "",
      createTimeDate: null,
      showCreateTimeDateError: "",
      endTimeDate: null,
      showEndTimeDateError: "",
      payOption: [],
      id: "",
      refId: "",
      loading: false,
      exporting: false,
      form: {
        orderId: "",
        merchantOrderId: "",
        channelOrderId: "",
        appId: "",
        channelId: "",
        startCreateDate: "",
        endCreateDate: "",
        startEndDate: "",
        endEndDate: "",
        status: "",
        callbackStatus: "",
        amount: "",
        settlementStatus: "",
        showOrderCount: "01",
        checkoutSubmit: "",
        upstream: "",
        isCardGroup: false,
        refId: "",
        merchantUserId: "",
        transactionMapped: "",
      },
      formRules: {
        createTimeDate: [
          {
            type: "date",
            trigger: "change",
            validator: this.validateDate,
          },
        ],
        endTimeDate: [
          {
            type: "date",
            trigger: "change",
            validator: this.validateDate,
          },
        ],
      },
      showDialog: false,
      formData: null,
      totalAmount: "",
      totalFee: "",
      totalActualAmount: "",
      merchantList: [],
      agentList: [],
      isShowModifyDialog: false,
      isShowManualSettlementDialog: false,
      isShowCreateTestCollectionOrderDialog: false,
      isShowOrderRestorationDialog: false,
      isShowSyncOrderStatusConfigDialog: false,
      isShowOrderLogDialog: false,
      isShowOrderDetailDrawer:false,
      currentOrderDetail: null,
      columns: generateColumns(),
    };
  },
  methods: {
    createTimeChange(e) {
      if (e) {
        this.form.startCreateDate = e[0];
        this.form.endCreateDate = e[1];
      } else {
        this.form.startCreateDate = "";
        this.form.endCreateDate = "";
      }
    },
    endTimeChange(e) {
      if (e) {
        this.form.startEndDate = e[0];
        this.form.endEndDate = e[1];
      } else {
        this.form.startEndDate = "";
        this.form.endEndDate = "";
      }
    },
    reset() {
      let start = moment().format("yyyyMMDD");
      let end = moment().format("yyyyMMDD");
      this.form.startCreateDate = start;
      this.form.endCreateDate = end;
      this.createTimeDate = [start, end];

      this.form.startEndDate = "";
      this.form.endEndDate = "";
      this.endTimeDate = null;

      this.form.showOrderCount = "01";

      this.totalAmount = "";
      this.totalFee = "";
      this.totalActualAmount = "";
    },
    handleModify(row) {
      this.id = row.orderId;
      this.isShowModifyDialog = true;
      this.refId = row.refId;
    },
    handleOrderLog(row) {
      this.id = row.orderId;
      this.isShowOrderLogDialog = true;
      this.currentOrderDetail = row
    },
    handleManualSettlement() {
      this.isShowManualSettlementDialog = true;
    },
    handleCreateTestingRepaymentLink() {
      this.isShowCreateTestCollectionOrderDialog = true;
    },
    handleOrderRestoration() {
      this.isShowOrderRestorationDialog = true;
    },
    handleSyncOrderStatusConfig() {
      this.isShowSyncOrderStatusConfigDialog = true;
    },
    add() {
      this.action = "addMerchant";
      this.formData = null;
      this.showDialog = true;
    },
    edit(row) {
      this.action = "editMerchant";
      this.formData = row;
      this.showDialog = true;
    },
    orderRefresh(row) {
      let orderId = row.orderId;

      this.$confirm(
        this.$t("message.confirmSyncingOrder"),
        this.$t("text.prompt"),
        {
          confirmButtonText: this.$t("text.confirm"),
          cancelButtonText: this.$t("text.cancel"),
          type: "warning",
        }
      )
        .then(() => {
          this.axios
            .post("/order/syncOrderStatus", {
              orderId: orderId,
            })
            .then((res) => {
              if (res.code === 200) {
                this.$bus.$emit("refresh"); //通知刷新
                this.$notify({
                  title: this.$t("text.prompt"),
                  message: res.msg,
                  type: "success",
                });
              } else {
                this.$notify.error({
                  title: this.$t("text.prompt"),
                  message: res.msg,
                });
              }
            });
        })
        .catch(() => {});
    },
    orderNotify(row) {
      let orderId = row.orderId;

      this.$confirm(
        this.$t("message.confirmNotifyingOrder"),
        this.$t("text.prompt"),
        {
          confirmButtonText: this.$t("text.confirm"),
          cancelButtonText: this.$t("text.cancel"),
          type: "warning",
        }
      )
        .then(() => {
          this.axios
            .post("/order/notifyOrder", { orderId: orderId })
            .then((res) => {
              if (res.code === 200) {
                this.$bus.$emit("refresh"); //通知刷新
                this.$notify({
                  title: this.$t("text.operationSuccessful"),
                  message:this.$t("text.requestSubmittedSuccessMessage") ,
                  type: "success",
                });
              } else {
                this.$notify.error({
                  title: this.$t("text.prompt"),
                  message: res.msg,
                });
              }
            });
        })
        .catch(() => {});
    },
    orderReverse(orderId) {
      console.log("orderReverse orderId: " + orderId);

      const handleReverse = (params) => {
        this.axios
            .post("/order/reverseOrderStatus", params)
            .then((res) => {
              if (res.code === 200) {
                this.$bus.$emit("refresh"); //通知刷新
                this.$notify({
                  title: this.$t("text.operationSuccessful"),
                  message: this.$t("text.updateSuccessMessage"),
                  type: "success",
                });
              } else {
                this.$notify.error({
                  title: this.$t("text.prompt"),
                  message: res.msg,
                });
              }
            });
      };

      if (this.$store.state.isBD) {
        this.$prompt(this.$createElement('span', {style:'white-space: pre-line;'},  this.$t("message.confirmReverseOrderMsg",{ orderId }) + this.$t('message.inputRefId')), this.$t('text.prompt'), {
          confirmButtonText: this.$t('text.confirm'),
          cancelButtonText: this.$t('text.cancel'),
          type: "warning",
          inputPattern: /^[A-Za-z0-9]+$/,
          inputErrorMessage: this.$t('message.inputRefId')
        })
            .then(({ value }) => handleReverse({ orderId, refId: value }))
            .catch(() => {});
      } else {
        this.$confirm(
            this.$createElement('span', {style:'white-space: pre-line;'},  this.$t("message.confirmReverseOrderMsg",{ orderId })),
            this.$t("message.confirmReverseOrderTitle"),

            {
              confirmButtonText: this.$t("text.confirm"),
              cancelButtonText: this.$t("text.cancel"),
              type: "warning",
            }
        )
        .then(() => handleReverse({ orderId }))
        .catch(() => {});
      }
    },
    countAmount() {
      this.loading = true;
      this.clearCountAmount();

      let param = {};

      let showOrderCount = this.form.showOrderCount;
      if (showOrderCount !== "01") {
        this.totalAmount = "";
        this.totalFee = "";
        this.totalActualAmount = "";
        return;
      }

      for (let key in this.form) {
        let value = this.form[key];
        if (value !== "" && value !== null) {
          param[key] = value;
        }
      }
      param.payType = 110;
      this.$axios.get("/order/amountCount", { params: param }).then((res) => {
        if (res.code === 200) {
          this.totalAmount = this.$formatter.FenToYuanToThousandth(
            null,
            null,
            res.data.amount,
            null
          );
          this.totalFee = this.$formatter.FenToYuanToThousandth(
            null,
            null,
            res.data.fee,
            null
          );
          this.totalActualAmount = this.$formatter.FenToYuanToThousandth(
            null,
            null,
            res.data.actualAmount,
            null
          );
        }else if (res.code === 500) {
          this.$notify.error({
            title: this.$t("text.prompt"),
            message: res.msg,
          });
        }else {
          this.$notify.error({
            title: this.$t("text.prompt"),
            message: this.$t("message.queryFailed"),
          });
        }
      }).finally(() => {
        this.loading = false;
      });
    },
    clearCountAmount() {
      this.totalAmount = "";
      this.totalFee = "";
      this.totalActualAmount = "";
    },
    exportToS3Detail() {
      this.loading = true;
      this.exporting = true;
      this.axios
        .get("/order/selectListForExportToS3?payType=110", {
          params: this.form,
        })
        .then((res) => {
          if (res.code === 200) {
            this.$notify.success({
              title: this.$t("text.exportZipTitle"),
              message: this.$t("text.exportZipMessage"),
            });
            this.downloadExportZipFile();
          } else {
            this.$notify.error({
              title: this.$t("text.prompt"),
              message: res.msg,
            });
          }
        })
        .catch((error) => {
          console.log(error);
          this.$notify.error({
            title: this.$t("text.prompt"),
            message: this.$t("message.exportFailed"),
          });
        });
      this.exporting = false;
      this.loading = false;
    },
    downloadExportZipFile() {
      this.axios
        .get("/order/getExportOrderZipFileS3Url", { params: this.form })
        .then((res) => {
          if (res.code === 200) {
            window.location.href = res.data;
            this.$notify.closeAll();
          } else {
            this.$notify.error({
              title: this.$t("text.prompt"),
              message: res.msg,
            });
          }
        })
        .catch((error) => {
          console.log(error);
          this.$notify.error({
            title: this.$t("text.prompt"),
            message: this.$t("message.downloadFailed"),
          });
        });
    },
    exportDetail() {
      this.loading = true;
      this.exporting = true;
      this.axios
        .get("/order/selectListForExport?payType=110", {
          params: this.form,
          responseType: "blob",
          timeout: 30 * 60 * 1000,
        })
        .then((res) => {
          if (res.data) {
            // 回excel資料，瀏覽器下載
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              this.$t("text.collectionOrders.xlsx")
            );
            document.body.appendChild(link);
            link.click();
          } else if (res.text) {
            // 回JSON，開彈窗
            res
              .text()
              .then(JSON.parse)
              .then((res) => {
                if (res.code !== 200) {
                  this.$message({
                    message: res.msg,
                    type: "error",
                  });
                } else {
                  this.$message({
                    message: res.msg,
                    type: "success",
                  });
                }
              });
          }
          this.exporting = false;
        })
        .catch((error) => {
          console.log(error);
          this.$notify.error({
            title: this.$t("text.prompt"),
            message: this.$t("message.exportFailed"),
          });
          this.exporting = false;
        });
      this.loading = false;
    },
    exportExcel(value) {
      switch (value) {
        case "exportDetail":
          this.exportDetail();
          break;
        case "exportToS3Detail":
          this.exportToS3Detail();
          break;
        case "downloadExportZipFile":
          this.downloadExportZipFile();
          break;
        default:
          console.warn(`Unknown command: ${value}`);
      }
    },
    popBatchBankStatementFileSelect() {
      this.$refs.bankStatementFile.click();
    },
    popBatchCallbackFileSelect() {
      this.$refs.callbackFile.click();
    },
    handleBatchCallbackUpdate() {
      const file = this.$refs.callbackFile;
      const formData = new FormData();
      formData.append("file", file.files[0]);
      this.axios
        .post("/order/bulkUpdateOrderStatus", formData, {
          transformRequest: [
            function (data, headers) {
              delete headers.post["Content-Type"];
              return data;
            },
          ],
        })
        .then((response) => {
          console.log(response);
          this.$notify({
            title: this.$t("text.prompt"),
            message: response.msg,
            type: "success",
          });
          file.value = "";
        })
        .catch((error) => {
          console.log(error);
          this.$notify.error({
            title: this.$t("text.prompt"),
            message: this.$t("message.uploadFailed"),
          });
          file.value = "";
        });
    },
    popSyncFileSelect() {
      this.$refs.SyncFile.click();
    },
    handleBulkSyncOrderStatus() {
      const file = this.$refs.SyncFile;
      const formData = new FormData();
      formData.append("file", file.files[0]);
      this.axios
        .post("/order/bulkSyncOrderStatus", formData, {
          transformRequest: [
            function (data, headers) {
              delete headers.post["Content-Type"];
              return data;
            },
          ],
        })
        .then((response) => {
          console.log(response);
          this.$notify({
            title: this.$t("text.prompt"),
            message: response.msg,
            type: "success",
          });
          file.value = "";
        })
        .catch((error) => {
          console.log(error);
          this.$notify.error({
            title: this.$t("text.prompt"),
            message: this.$t("message.uploadFailed"),
          });
          file.value = "";
        });
    },
    importStatement() {
      const file = this.$refs.bankStatementFile;
      const formData = new FormData();
      formData.append("file", file.files[0]);
      this.axios
        .post("/order/importStatement", formData, {
          transformRequest: [
            function (data, headers) {
              delete headers.post["Content-Type"];
              return data;
            },
          ],
        })
        .then((response) => {
          console.log(response);
          this.$notify({
            title: this.$t("text.prompt"),
            message: response.msg,
            type: "success",
          });
          file.value = "";
        })
        .catch((error) => {
          console.log(error);
          this.$notify.error({
            title: this.$t("text.prompt"),
            message: this.$t("message.uploadFailed"),
          });
          file.value = "";
        });
    },
    validateDate(rule, value, callback) {
      if (!this.createTimeDate && !this.endTimeDate) {
        this.showCreateTimeDateError = this.$t(
          "message.creationTimeOrFinishTimeRequiredError"
        );
        this.showEndTimeDateError = this.$t(
          "message.creationTimeOrFinishTimeRequiredError"
        );
        callback(
          new Error(this.$t("message.creationTimeOrFinishTimeRequiredError"))
        );
      } else {
        this.showCreateTimeDateError = "";
        this.showEndTimeDateError = "";
        callback();
      }
    },
    handleShowOrderDetail(data) {
      this.isShowOrderDetailDrawer = true;
      this.currentOrderDetail = data;
    },
    handleColumnChange(value) {
      this.columns = value;
    },
  },
  created() {
    this.reset();

    if(ROLE_TYPE.isManager() || ROLE_TYPE.isAgent()){
      this.$axios
          .get("/merchant/selectListForOption", { params: {} })
          .then((res) => {
            this.merchantList = res.data;
          });
    }

    if (ROLE_TYPE.isManager()){
      this.$axios
          .get("/agent/selectAgentForOption", { params: {} })
          .then((res) => {
            this.agentList = res.data;
          });

      this.$axios
          .get("/routeChannelInfo/selectByCondition", { params: {} })
          .then((res) => {
            res.data.map((item) => {
              if (item.productId === "110") {
                this.payOption.push(item);
              }
            });
          });
    }
  },
};
</script>

<style scoped>
.remark {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  word-break: break-all;
}

.orderStyle {
  text-align: left;
}
::v-deep .el-table__row {
  cursor: pointer;
}
.table-set-button-list {
  display: flex;
  justify-content: flex-end;
}
</style>
